import { CalendarDate, ZonedDateTime } from '@internationalized/date';
import { useCalendarCell } from '@react-aria/calendar';
import { CalendarState } from '@react-stately/calendar';
import { useRef } from 'react';

import { LIMIT_ORDER_MAX_EXPIRATION_DAYS } from '@/store/limitOrder';

import { fontStyles } from '@/styles/typography';

import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

interface CalendarCellProps {
  state: CalendarState;
  date: CalendarDate;
  now: ZonedDateTime;
}
/**
 * Returns a styled cell for the specified calendar date.
 */
export function Cell({ state, date, now }: CalendarCellProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const hasDatePassed = date.compare(now) < 0;
  const { cellProps, buttonProps, isSelected, isDisabled, formattedDate, isOutsideVisibleRange } =
    useCalendarCell({ date }, state, ref);

  const cellDataState = isOutsideVisibleRange
    ? 'outside'
    : isDisabled
      ? 'disabled'
      : isSelected
        ? 'selected'
        : undefined;

  if (isDisabled && !isOutsideVisibleRange && !hasDatePassed) {
    return (
      <MatchaTooltip>
        <MatchaTooltipTrigger>
          <td {...cellProps}>
            <div
              {...buttonProps}
              ref={ref}
              className="h-7 w-7 inline-flex items-center m-0.5 nth-[7n]:mr-0"
            >
              <div
                data-state={cellDataState}
                className={fontStyles({
                  variant: 'BODY_MD',
                  class:
                    'flex items-center h-full w-full justify-center rounded-sm text-token-gray-1200 hover:bg-token-gray-400 hover:cursor-pointer focus:outline-token-gray-1200 focus:outline-[2px] data-[state=disabled]:bg-transparent data-[state=disabled]:text-token-gray-600 data-[state=disabled]:cursor-not-allowed data-[state=selected]:text-token-gray-200 data-[state=selected]:bg-token-purple-900 data-[state=outside]:bg-transparent data-[state=outside]:cursor-not-allowed',
                })}
              >
                {formattedDate}
              </div>
            </div>
          </td>
        </MatchaTooltipTrigger>
        <MatchaTooltipContent>
          Matcha only supports custom dates up to {LIMIT_ORDER_MAX_EXPIRATION_DAYS} days.
        </MatchaTooltipContent>
        <MatchaTooltipPopover />
      </MatchaTooltip>
    );
  } else {
    return (
      <td {...cellProps}>
        <div
          {...buttonProps}
          ref={ref}
          className="h-7 w-7 inline-flex items-center m-0.5 nth-[7n]:mr-0"
        >
          <div
            data-state={cellDataState}
            className={fontStyles({
              variant: 'BODY_MD',
              class:
                'flex items-center h-full w-full justify-center rounded-sm text-token-gray-1200 hover:bg-token-gray-400 hover:cursor-pointer focus:outline-token-gray-1200 focus:outline-[2px] data-[state=disabled]:bg-transparent data-[state=disabled]:text-token-gray-600 data-[state=disabled]:cursor-not-allowed data-[state=selected]:text-token-gray-200 data-[state=selected]:bg-token-purple-900 data-[state=outside]:bg-transparent data-[state=outside]:cursor-not-allowed',
            })}
          >
            {isOutsideVisibleRange ? '' : formattedDate}
          </div>
        </div>
      </td>
    );
  }
}
